import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import { Raumstandhinweis } from './Raumstandhinweis';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, PropertyGrid, StatusBadge, WertEinheit,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Rettungsweg und Sicherheitsbeleuchtung nach DIN EN 1838 (Paket V-E5).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIESE SEITE ZEIGT — UND WAS SIE NICHT ZEIGT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sie zeigt je Raum, ob sich aus dem Türbuch ein Rettungsweg zu einem
 * Treppenhaus oder einem Ausgang ins Freie ableiten lässt, und ob auf diesem
 * Weg ein Rettungszeichen und eine Sicherheitsleuchte stehen.
 *
 * Sie zeigt NICHT, ob die Beleuchtungsstärke von 1 lx auf der Wegmitte
 * erreicht wird — dafür fehlen Leuchtenposition und Lichtstärkeverteilung.
 * Geprüft wird nur das VORHANDENSEIN, wie beim Beleuchtungsnachweis das
 * Wirkungsgradverfahren nur den Mittelwert liefert.
 *
 * Der Rettungsweg selbst stützt sich NICHT auf Fluchtrichtung/Anschlag im
 * Türbuch — die sind projektweit gleich gesetzt und unterscheiden nichts
 * (siehe KG-450-Anlagenliste). Er kommt aus der RAUMFOLGE: welche Türen
 * welche Räume verbinden, ungeachtet ihrer Aufnahmerichtung.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIESELBE RENDERSCHLEIFE WIE AUF DER BELEUCHTUNGSSEITE (Paket U6b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `const zeilen: any[] = d?.zeilen ?? []` erzeugte auch hier bei jedem
 * Durchlauf ein neues Feld, an dem der Effekt für die Geschossliste hing.
 * Zweite Fundstelle derselben Fehlerklasse; gemerkt wird jetzt mit
 * `useMemo`.
 */
export function ElektroSicherheitsbeleuchtung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const [befund, setBefund] = useState('');
  const [geschoss, setGeschoss] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [zeigeAnnahmen, setZeigeAnnahmen] = useState(false);

  const laden = useCallback(() => {
    if (!projekt) return;
    setFehler(null);

    api.elektroSicherheitsbeleuchtung(projekt.id, {
      befund: befund || undefined,
      geschoss: geschoss || undefined,
    })
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, befund, geschoss]);

  useEffect(() => { laden(); }, [laden]);

  const zeilen: any[] = useMemo(() => d?.zeilen ?? [], [d]);
  const lauf = d?.lauf ?? null;
  const hinweise: string[] = useMemo(() => d?.hinweise ?? [], [d]);
  const annahmen: any[] = useMemo(() => d?.annahmen ?? [], [d]);

  const [geschosse, setGeschosse] = useState<string[]>([]);

  useEffect(() => {
    if (befund || geschoss) return;

    setGeschosse([...new Set(zeilen
      .map((z) => z.geschoss)
      .filter((g: string | null): g is string => !!g))].sort());
  }, [zeilen, befund, geschoss]);

  const raum = useMemo(
    () => zeilen.find((z) => z.id === gewaehlt) ?? null,
    [zeilen, gewaehlt]);

  const zahl = (w: number | null | undefined, n = 0) =>
    w === null || w === undefined
      ? '–'
      : w.toLocaleString('de-DE',
          { minimumFractionDigits: n, maximumFractionDigits: n });

  const befundZustand = (b: string): Zustand =>
    (b === 'erfuellt_stichprobe' ? 'gut'
      : b === 'kein_pfad_ableitbar' ? 'fehler'
        : ['rettungszeichen_fehlt', 'sicherheitsleuchte_fehlt',
           'antipanik_ungedeckt'].includes(b) ? 'warnung'
          : 'neutral');

  const befundtext = (b: string) => ({
    erfuellt_stichprobe: 'erfüllt (Stichprobe)',
    kein_pfad_ableitbar: 'kein Pfad ableitbar',
    rettungszeichen_fehlt: 'Rettungszeichen fehlt',
    sicherheitsleuchte_fehlt: 'Sicherheitsleuchte fehlt',
    antipanik_ungedeckt: 'Antipanik ungedeckt',
  } as Record<string, string>)[b] ?? b;

  const spalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'geschoss', titel: 'Geschoss', breite: '110px', wert: (z) => z.geschoss },
    {
      schluessel: 'raum', titel: 'Raum', immerSichtbar: true,
      wert: (z) => `${z.raumnummer ?? ''} ${z.raumname ?? ''}`.trim(),
    },
    {
      schluessel: 'weg', titel: 'Weg zum Ziel',
      wert: (z) => (z.erreichbar
        ? `${z.zielRaumnummer ?? ''} ${z.zielRaumname ?? ''}`.trim() : ''),
      inhalt: (z) => (
        <span className="schwach">
          {z.erreichbar ? `${z.zielRaumnummer ?? '–'} ${z.zielRaumname ?? ''}` : '–'}
        </span>
      ),
    },
    {
      schluessel: 'tueren', titel: 'Türen', art: 'zahl', breite: '90px',
      titelHinweis: 'Zahl der Türen bis zum Zielraum', wert: (z) => z.hopsZumZiel,
    },
    {
      schluessel: 'rettungszeichen', titel: 'Rettungszeichen', art: 'zahl',
      wert: (z) => z.rettungszeichenAnzahl,
    },
    {
      schluessel: 'sicherheitsleuchten', titel: 'Sicherheitsleuchten', art: 'zahl',
      wert: (z) => z.sicherheitsleuchteAnzahl,
    },
    {
      schluessel: 'antipanik', titel: 'Antipanik', art: 'status', breite: '200px',
      wert: (z) => (z.antipanikErforderlich
        ? (z.antipanikGedeckt ? 'erforderlich, gedeckt' : 'erforderlich, ungedeckt')
        : 'nicht erforderlich'),
      inhalt: (z) => (z.antipanikErforderlich
        ? (z.antipanikGedeckt
          ? <StatusBadge zustand="gut" text="erforderlich, gedeckt" klein />
          : <StatusBadge zustand="warnung" text="erforderlich, ungedeckt" klein />)
        : <span className="schwach">nicht erforderlich</span>),
    },
    {
      schluessel: 'befund', titel: 'Befund', art: 'status', breite: '210px',
      wert: (z) => befundtext(z.befund),
      inhalt: (z) => (
        <StatusBadge zustand={befundZustand(z.befund)} text={befundtext(z.befund)} klein />
      ),
    },
  ], []);

  const annahmenspalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
      wert: (a) => a.schluessel, inhalt: (a) => <code>{a.schluessel}</code>,
    },
    {
      schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
      wert: (a) => (a.zahlwert ?? a.wert),
      inhalt: (a) => (a.zahlwert !== null && a.zahlwert !== undefined
        ? <WertEinheit wert={a.zahlwert} nachkomma={2} einheit={a.einheit ?? undefined} rechts />
        : <>{a.wert ?? '–'}</>),
    },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (a) => a.herkunft },
    {
      schluessel: 'normbezug', titel: 'Normbezug', wert: (a) => a.normbezug,
      inhalt: (a) => <span className="schwach">{a.normbezug ?? '–'}</span>,
    },
    {
      schluessel: 'begruendung', titel: 'Begründung', wert: (a) => a.begruendung,
      inhalt: (a) => <span className="schwach">{a.begruendung ?? '–'}</span>,
    },
  ], []);

  const kopf = (
    <PageHeader
      titel="Sicherheitsbeleuchtung — Rettungsweg je Raum"
      beschreibung={'Rettungsweg aus dem Türbuch (Raumfolge, nicht '
        + 'Fluchtrichtung/Anschlag) zum nächsten Treppenhaus oder Ausgang ins Freie, '
        + 'dazu Rettungszeichen- und Sicherheitsleuchten je Raum nach DIN EN 1838.'}
      meta={lauf ? (
        <>
          Gerechnet am {new Date(lauf.gerechnetAm).toLocaleString('de-DE')}
          {lauf.gerechnetVon ? ` von ${lauf.gerechnetVon}` : ''}
        </>
      ) : undefined}
      aktionen={(projekt && lauf) ? (
        <>
          <button
            className="m-knopf m-knopf-haupt"
            onClick={() => api.elektroSicherheitsbeleuchtungPdf(projekt.id)}
          >
            Nachweis ELT-SIB als PDF
          </button>
          <button className="m-knopf" onClick={() => setZeigeAnnahmen((v) => !v)}>
            {zeigeAnnahmen ? 'Annahmen ausblenden' : 'Annahmen nach DIN EN 1838'}
          </button>
        </>
      ) : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Sicherheitsbeleuchtung konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        {kopf}
        <LoadingState text="Rettungswege werden geladen …" zeilen={8} spalten={6} />
      </>
    );
  }

  if (!lauf) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Für dieses Modell wurde die Sicherheitsbeleuchtung noch nicht geprüft."
          erklaerung={d.meldung
            ?? 'Der Nachweis leitet den Rettungsweg aus den Türverbindungen des Türbuchs '
            + 'ab; ohne aufgenommene Türen gibt es keinen Weg zu prüfen.'}
          aktion={(
            <button className="m-knopf m-knopf-haupt" onClick={laden}>
              Erneut nachsehen
            </button>
          )}
        />
      </>
    );
  }

  return (
    <>
      {kopf}
      <Raumstandhinweis api={api} projektId={projekt.id} rechnungen={['sicherheitsbeleuchtung']} />

      <div className="hinweis warnung" style={{ display: 'block', maxWidth: 860 }}>
        <strong>Nur Vorhandensein, keine Beleuchtungsstärke.</strong> Geprüft
        wird, ob je Raum auf dem abgeleiteten Weg ein Rettungszeichen und eine
        Sicherheitsleuchte stehen — nicht, ob 1 lx auf der Wegmitte erreicht
        wird oder die Erkennungsweite eines Zeichens für den realen
        Sichtabstand reicht. {lauf.raeumeOhnePfad} von {lauf.raeumeGesamt}{' '}
        Räumen haben aus dem Türbuch gar keinen ableitbaren Weg (keine
        belastbare Türverbindung).
      </div>

      {/* --- Kennzahlen ---------------------------------------------------- */}
      <KpiReihe>
        <KpiCard titel="Räume geprüft" wert={lauf.raeumeGesamt} fussnote="DIN EN 1838" />
        <KpiCard
          titel="mit abgeleitetem Weg" wert={lauf.raeumeErreichbar}
          vergleich={`von ${zahl(lauf.raeumeGesamt)} Räumen`}
        />
        <KpiCard
          titel="ohne Pfad" wert={lauf.raeumeOhnePfad}
          zustand={lauf.raeumeOhnePfad > 0 ? 'fehler' : 'gut'}
          zustandText={lauf.raeumeOhnePfad > 0 ? 'keine belastbare Türverbindung' : 'keiner'}
        />
        <KpiCard
          titel="Zielräume" wert={lauf.zielraeumeGesamt}
          vergleich={`${zahl(lauf.zielraeumeTreppenhaus)} Treppenhaus, `
            + `${zahl(lauf.zielraeumeAussenausgang)} Ausgang`}
        />
        <KpiCard
          titel="erfüllt (Stichprobe)" wert={lauf.erfuelltStichprobe}
          zustand="gut" zustandText="Stichprobe erfüllt"
        />
        <KpiCard
          titel="Rettungszeichen fehlt" wert={lauf.rettungszeichenFehlt}
          zustand={lauf.rettungszeichenFehlt > 0 ? 'warnung' : 'gut'}
          zustandText={lauf.rettungszeichenFehlt > 0 ? 'nachsehen' : 'keines'}
        />
        <KpiCard
          titel="Sicherheitsleuchte fehlt" wert={lauf.sicherheitsleuchteFehlt}
          zustand={lauf.sicherheitsleuchteFehlt > 0 ? 'warnung' : 'gut'}
          zustandText={lauf.sicherheitsleuchteFehlt > 0 ? 'nachsehen' : 'keine'}
        />
        <KpiCard
          titel="Türen mit belastbarer Kante" wert={lauf.tuerenKanteZuverlaessig}
          vergleich={`von ${zahl(lauf.tuerenGesamt)} Türen · `
            + `${zahl(lauf.tuerenAusgeschlossenMehrdeutig)} mehrdeutig ausgeschlossen`}
        />
      </KpiReihe>

      {/* --- Die Hinweise des Laufs ---------------------------------------- */}
      {hinweise.length > 0 && (
        <ContentSection titel="Was der Lauf zu seinen eigenen Zahlen sagt">
          <ul className="schwach" style={{ maxWidth: 860 }}>
            {hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </ContentSection>
      )}

      {/* --- Die Annahmen ---------------------------------------------------- */}
      {zeigeAnnahmen && (
        <ContentSection titel="Annahmen nach DIN EN 1838" nackt>
          <DataTable
            name="Annahmen"
            spalten={annahmenspalten}
            zeilen={annahmen}
            schluessel={(a) => String(a.schluessel)}
            ausfuhr
            ausfuhrDateiname="sicherheitsbeleuchtung-annahmen"
            leer={(
              <EmptyState
                titel="Für diesen Lauf sind keine Annahmen hinterlegt."
                erklaerung="Annahmen halten fest, welcher Normwert ohne Projektfestlegung angesetzt wurde."
              />
            )}
          />
        </ContentSection>
      )}

      {/* --- Der Nachweis je Raum ------------------------------------------ */}
      <DataTable
        name="Rettungsweg je Raum"
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(z) => String(z.id)}
        suchePlatzhalter="Raum oder Ziel"
        ausfuhr
        ausfuhrDateiname="sicherheitsbeleuchtung"
        beiZeileKlick={(z) => setGewaehlt(z.id === gewaehlt ? null : z.id)}
        aktiveZeile={gewaehlt ?? undefined}
        werkzeuge={(
          <>
            <select value={befund} onChange={(e) => setBefund(e.target.value)} aria-label="Befund" style={{ width: 'auto' }}>
              <option value="">alle Befunde</option>
              <option value="erfuellt_stichprobe">erfüllt (Stichprobe)</option>
              <option value="kein_pfad_ableitbar">kein Pfad ableitbar</option>
              <option value="rettungszeichen_fehlt">Rettungszeichen fehlt</option>
              <option value="sicherheitsleuchte_fehlt">Sicherheitsleuchte fehlt</option>
              <option value="antipanik_ungedeckt">Antipanik ungedeckt</option>
            </select>

            <select value={geschoss} onChange={(e) => setGeschoss(e.target.value)} aria-label="Geschoss" style={{ width: 'auto' }}>
              <option value="">alle Geschosse</option>
              {geschosse.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </>
        )}
        leer={(
          <EmptyState
            titel={befund || geschoss
              ? 'Kein Raum passt zu diesem Filter.'
              : 'Der Lauf hat keinen Raum bewertet.'}
            erklaerung={befund || geschoss
              ? 'Befund- und Geschossfilter wirken auf dem Server und schränken die Menge ein.'
              : 'Ohne Räume im Raumbuch gibt es keinen Rettungsweg abzuleiten.'}
            aktion={(befund || geschoss) ? (
              <button className="m-knopf" onClick={() => { setBefund(''); setGeschoss(''); }}>
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />

      {/* --- Die gewählte Zeile -------------------------------------------- */}
      {raum && (
        <ContentSection
          titel={`${raum.raumnummer} ${raum.raumname}`}
          beschreibung={raum.befundtext}
          aktionen={(
            <button className="m-knopf m-knopf-still" onClick={() => setGewaehlt(null)}>
              Schließen
            </button>
          )}
        >
          <PropertyGrid
            spalten={2}
            eintraege={[
              {
                beschriftung: 'Weg',
                wert: raum.erreichbar
                  ? `${raum.hopsZumZiel} Tür(en) bis ${raum.zielRaumnummer} ${raum.zielRaumname ?? ''}`
                  : 'kein Weg aus dem Türbuch ableitbar',
                breit: true,
              },
              { beschriftung: 'Fläche', wert: raum.flaecheM2, einheit: 'm²', nachkomma: 1, herkunft: 'modell' },
              { beschriftung: 'Rettungszeichen', wert: raum.rettungszeichenAnzahl, einheit: 'Stück', herkunft: 'modell' },
              { beschriftung: 'Sicherheitsleuchten', wert: raum.sicherheitsleuchteAnzahl, einheit: 'Stück', herkunft: 'modell' },
              {
                beschriftung: 'Soll auf der Wegmitte',
                wert: raum.mindestbeleuchtungsstaerkeSollLx, einheit: 'lx', nachkomma: 2,
                hinweis: 'nicht gerechnet — Vorhandensein geprüft, nicht die Beleuchtungsstärke',
              },
              {
                beschriftung: 'Antipanik',
                wert: raum.antipanikErforderlich
                  ? (
                    <StatusBadge
                      zustand={raum.antipanikGedeckt ? 'gut' : 'warnung'}
                      text={raum.antipanikGedeckt ? 'erforderlich, gedeckt' : 'erforderlich, ungedeckt'}
                      klein
                    />
                  )
                  : 'nicht erforderlich',
                hinweis: raum.antipanikErforderlich
                  ? `Fläche über ${zahl(raum.antipanikFlaechenschwelleM2SollWert, 0)} m²`
                  : undefined,
              },
              {
                beschriftung: 'Antipanik-Soll',
                wert: raum.antipanikMindestbeleuchtungsstaerkeSollLx, einheit: 'lx', nachkomma: 2,
                hinweis: 'nicht gerechnet',
              },
              {
                beschriftung: 'Erkennungsweite', wert: raum.erkennungsweiteAnnahmeM,
                einheit: 'm', nachkomma: 1,
                hinweis: 'Annahme, keine geprüfte Sichtachse',
              },
            ]}
          />
        </ContentSection>
      )}
    </>
  );
}
